import { Suspense, lazy } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";

// Lazy load toast components - they're not needed for initial render
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import Index from "./pages/Index";

// Lazy load all pages except Index for code splitting
const Portfolio = lazy(() => import("./pages/Portfolio"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Services = lazy(() => import("./pages/Services"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminEnquiries = lazy(() => import("./pages/admin/Enquiries"));
const AdminBookings = lazy(() => import("./pages/admin/Bookings"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminMessages = lazy(() => import("./pages/admin/Messages"));
const AdminDocuments = lazy(() => import("./pages/admin/Documents"));
const AdminClients = lazy(() => import("./pages/admin/Clients"));
const AdminContracts = lazy(() => import("./pages/admin/Contracts"));
const AdminHomepageVideos = lazy(() => import("./pages/admin/HomepageVideos"));
const AdminProjectTrackers = lazy(() => import("./pages/admin/ProjectTrackers"));
const AdminFinancialYear = lazy(() => import("./pages/admin/FinancialYear"));
const AdminDeployments = lazy(() => import("./pages/admin/Deployments"));

const ClientBookings = lazy(() => import("./pages/client/Bookings"));
const ClientInvoices = lazy(() => import("./pages/client/Invoices"));
const ClientMedia = lazy(() => import("./pages/client/Media"));
const ClientQuestionnaires = lazy(() => import("./pages/client/Questionnaires"));
const ClientProfile = lazy(() => import("./pages/client/Profile"));
const ClientMessages = lazy(() => import("./pages/client/Messages"));
const ClientDocuments = lazy(() => import("./pages/client/Documents"));
const ClientContracts = lazy(() => import("./pages/client/Contracts"));
const ClientSettings = lazy(() => import("./pages/client/Settings"));
const Weddings = lazy(() => import("./pages/services/Weddings"));
const Clubs = lazy(() => import("./pages/services/Clubs"));
const Property = lazy(() => import("./pages/services/Property"));
const Brands = lazy(() => import("./pages/services/Brands"));
const Events = lazy(() => import("./pages/services/Events"));
const SocialMedia = lazy(() => import("./pages/services/SocialMedia"));
const Modelling = lazy(() => import("./pages/services/Modelling"));
const BabyShower = lazy(() => import("./pages/services/BabyShower"));
const BridalShower = lazy(() => import("./pages/services/BridalShower"));
const Anniversary = lazy(() => import("./pages/services/Anniversary"));
const Engagement = lazy(() => import("./pages/services/Engagement"));
const LoveShoot = lazy(() => import("./pages/services/LoveShoot"));
const Festivals = lazy(() => import("./pages/services/Festivals"));
const LiveStream = lazy(() => import("./pages/services/LiveStream"));
const Drone = lazy(() => import("./pages/services/Drone"));
const RescheduleConsultation = lazy(() => import("./pages/RescheduleConsultation"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Suspense fallback={null}>
        <Toaster />
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/weddings" element={<Weddings />} />
              <Route path="/services/clubs" element={<Clubs />} />
              <Route path="/services/property" element={<Property />} />
              <Route path="/services/brands" element={<Brands />} />
              <Route path="/services/events" element={<Events />} />
              <Route path="/services/social-media" element={<SocialMedia />} />
              <Route path="/services/modelling" element={<Modelling />} />
              <Route path="/services/baby-shower" element={<BabyShower />} />
              <Route path="/services/bridal-shower" element={<BridalShower />} />
              <Route path="/services/anniversary" element={<Anniversary />} />
              <Route path="/services/engagement" element={<Engagement />} />
              <Route path="/services/love-shoot" element={<LoveShoot />} />
              <Route path="/services/festivals" element={<Festivals />} />
              <Route path="/services/live-stream" element={<LiveStream />} />
              <Route path="/services/drone" element={<Drone />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/bookings" element={<ClientBookings />} />
              <Route path="/dashboard/invoices" element={<ClientInvoices />} />
              <Route path="/dashboard/media" element={<ClientMedia />} />
              <Route path="/dashboard/questionnaires" element={<ClientQuestionnaires />} />
              <Route path="/dashboard/videos" element={<ClientMedia />} />
              <Route path="/dashboard/profile" element={<ClientProfile />} />
              <Route path="/dashboard/messages" element={<ClientMessages />} />
              <Route path="/dashboard/documents" element={<ClientDocuments />} />
              <Route path="/dashboard/contracts" element={<ClientContracts />} />
              <Route path="/dashboard/settings" element={<ClientSettings />} />
              <Route path="/admin/enquiries" element={<AdminEnquiries />} />
              <Route path="/admin/bookings" element={<AdminBookings />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/messages" element={<AdminMessages />} />
              <Route path="/admin/documents" element={<AdminDocuments />} />
              <Route path="/admin/clients" element={<AdminClients />} />
              <Route path="/admin/contracts" element={<AdminContracts />} />
              <Route path="/admin/homepage-videos" element={<AdminHomepageVideos />} />
              <Route path="/admin/project-trackers" element={<AdminProjectTrackers />} />
              <Route path="/admin/financial-year" element={<AdminFinancialYear />} />
              <Route path="/admin/deployments" element={<AdminDeployments />} />
              <Route path="/reschedule-consultation" element={<RescheduleConsultation />} />
              
              <Route path="/admin/*" element={<Dashboard />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
